import { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface MessageInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

export function MessageInput({ onSend, disabled }: MessageInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  return (
    <div className="bg-zinc-900 border-t border-zinc-800 px-4 py-3 flex items-end gap-3">
      <textarea
        ref={textareaRef}
        rows={1}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder="Message..."
        disabled={disabled}
        className={cn(
          'flex-1 resize-none rounded-2xl px-4 py-2.5 text-sm',
          'bg-zinc-800 text-zinc-100 placeholder-zinc-500',
          'focus:outline-none focus:ring-2 focus:ring-emerald-500/50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'max-h-[120px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700'
        )}
      />
      <button
        onClick={handleSend}
        disabled={!value.trim() || disabled}
        aria-label="Envoyer"
        className={cn(
          'shrink-0 rounded-full p-2.5 transition-all',
          value.trim() && !disabled
            ? 'bg-emerald-500 hover:bg-emerald-400 text-white'
            : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
        )}
      >
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
}
